import { Inbox, Plus, Flag, BookOpen, Milestone, ExternalLink, LogOut, LifeBuoy, UserSearch, Settings } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import { useTrackerEnabled } from '@/queries/tracker'
import { AvatarInitials } from '@/components/ui/avatar-initials'
import { EtainablLogo } from '@/components/etainabl-logo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

type ActiveMatcher = 'exact' | 'prefix' | ((path: string) => boolean)

function NavLink({
  to,
  icon,
  label,
  count,
  match = 'exact',
  search,
}: {
  to: string
  icon: ReactNode
  label: string
  count?: number
  match?: ActiveMatcher
  search?: Record<string, unknown>
}) {
  const path = useRouterState({ select: s => s.location.pathname })
  const active =
    typeof match === 'function'
      ? match(path)
      : match === 'prefix'
      ? path === to || path.startsWith(`${to}/`)
      : path === to

  return (
    <Link
      to={to as never}
      {...(search ? { search: search as never } : {})}
      className={cn(
        'group relative flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm transition-colors',
        active
          ? 'bg-brand-50 text-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {active && <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-brand-500" />}
      <span className="flex h-4 w-4 items-center justify-center">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="text-[11px] tabular-nums text-muted-foreground">{count}</span>
      )}
    </Link>
  )
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="px-2.5 mb-1 text-[11px] font-medium tracking-[0.06em] uppercase text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  )
}

function UserPill() {
  const { user, logout } = useAuth0()
  const name = (user?.name as string | undefined) ?? user?.email ?? 'You'
  const company = ((user as Record<string, unknown> | undefined)?.['https://app.etainabl.com/companyName'] as string | undefined) ?? 'Etainabl'
  const v2Url = import.meta.env.VITE_ETAINABL_UI_URL ?? 'http://localhost:8081'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left text-sm hover:bg-muted">
        <AvatarInitials name={name} size="md" />
        <span className="flex-1 min-w-0">
          <span className="block truncate text-foreground">{name}</span>
          <span className="block truncate text-[11px] text-muted-foreground">{company}</span>
        </span>
        <ExternalLink aria-hidden className="h-4 w-4 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <a href={v2Url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm">
            <ExternalLink className="h-4 w-4" />
            Open Etainabl
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="flex items-center gap-2 text-sm"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function Sidebar() {
  const { inbox, queue, isAdmin } = useTicketCounts()
  const asUser = useRouterState({ select: s => (s.location.search as { asUser?: string })?.asUser })
  const lensActive = Boolean(asUser)
  // Server-authoritative: the flag middleware 404s when the caller's company
  // lacks client-tracker, and grants staff a bypass — so this probe alone
  // decides visibility for both customers and admins. Threading `asUser`
  // matters here specifically: without it, a staffer lensing a flagless
  // company's user would still see the admin's own unlensed (bypassed) probe
  // result and the nav link would render for a tracker the lensed view 404s.
  const trackerEnabled = useTrackerEnabled(asUser)

  return (
    <aside className="flex h-screen w-[220px] shrink-0 flex-col gap-5 border-r border-border bg-background py-5 px-2">
      <Link to="/" className="flex flex-col items-center gap-1 px-2.5">
        <EtainablLogo variant="dark" className="h-5" />
        <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
          <LifeBuoy className="h-3.5 w-3.5" />
          Help Desk
        </span>
      </Link>

      {(!isAdmin || lensActive) && (
        <Section label="Tickets">
          <NavLink
            to="/tickets"
            icon={<Inbox className="h-4 w-4" />}
            label="Inbox"
            count={inbox}
            match={path => path === '/tickets' || path.startsWith('/tickets/')}
            search={{ asUser }}
          />
          <NavLink
            to="/tickets/new"
            icon={<Plus className="h-4 w-4" />}
            label="New ticket"
            match="exact"
          />
        </Section>
      )}

      <Section label="Knowledgebase">
        <NavLink
          to="/kb"
          icon={<BookOpen className="h-4 w-4" />}
          label="Articles"
          match={path => path === '/kb' || (path.startsWith('/kb/') && !path.startsWith('/kb/admin'))}
        />
      </Section>

      {trackerEnabled.data === true && (
        <Section label="Client Tracker">
          <NavLink
            to="/tracker"
            icon={<Milestone className="h-4 w-4" />}
            label="Tracker"
            match={path => path === '/tracker' || path.startsWith('/tracker/')}
            search={{ asUser }}
          />
        </Section>
      )}

      {isAdmin && (
        <Section label="Admin">
          <NavLink
            to="/admin"
            icon={<Flag className="h-4 w-4" />}
            label="Queue"
            count={queue}
            match={path =>
              path === '/admin' ||
              path.startsWith('/admin/tickets')
            }
          />
          <NavLink
            to="/admin/kb"
            icon={<BookOpen className="h-4 w-4" />}
            label="Manage articles"
            match={path => path === '/admin/kb' || path.startsWith('/admin/kb/')}
          />
          <NavLink
            to="/admin/view-as"
            icon={<UserSearch className="h-4 w-4" />}
            label="View as user"
            match="exact"
          />
          <NavLink
            to="/admin/settings"
            icon={<Settings className="h-4 w-4" />}
            label="Settings"
            match="exact"
          />
        </Section>
      )}

      <div className="mt-auto border-t border-border pt-2">
        <UserPill />
      </div>
    </aside>
  )
}
